import React, { useState } from "react";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ScanLine, Upload, CheckCircle2, AlertTriangle, X } from "lucide-react";
import { toast } from "sonner";

/**
 * AI upload + analyze card embedded inside the order form.
 * mode = "auftrag" → applies auftragsnummer/kls_id/datum/telekom_konto via onApply({auftragsnummer, kls_id, ausfuehrungsdatum, telekom_konto})
 * mode = "aufmass" → applies extracted positions via onApply({positionen})
 */
export default function AIAnalyzeCard({ mode, title, subtitle, onApply, dataTestid }) {
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  const choose = (f) => {
    setFile(f);
    setResult(null);
    if (!f) { setPreview(null); return; }
    if (f.type === "application/pdf" || f.name?.toLowerCase().endsWith(".pdf")) {
      setPreview({ pdf: true, name: f.name });
    } else {
      setPreview({ pdf: false, url: URL.createObjectURL(f) });
    }
  };

  const analyze = async () => {
    if (!file) { toast.error("Bitte zuerst ein Bild auswählen"); return; }
    setAnalyzing(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/ai/screenshot-analysis", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success("KI-Analyse fertig");
    } catch (err) { toast.error(formatErr(err.response?.data?.detail)); }
    finally { setAnalyzing(false); }
  };

  const apply = () => {
    if (!result) return;
    if (mode === "auftrag") {
      onApply?.({
        auftragsnummer: result.auftragsnummer || "",
        kls_id: result.kls_id || "",
        ausfuehrungsdatum: result.datum || "",
        telekom_konto: result.telekom_konto || "",
      });
      toast.success("Auftragsdaten übernommen");
    } else {
      const positionen = (result.positionen || []).map((p) => ({
        pos_nummer: String(p.pos_nummer || ""),
        beschreibung: p.beschreibung || "",
        menge: Number(p.menge || 0),
        einheit: p.einheit || "Stk",
      }));
      onApply?.({ positionen });
      toast.success(`${positionen.length} Position(en) übernommen`);
    }
  };

  const reset = () => { setFile(null); setPreview(null); setResult(null); };

  const conf = result?.konfidenz;
  const confColor = conf === "hoch" ? "text-emerald-700" : conf === "mittel" ? "text-amber-700" : "text-red-700";

  return (
    <div
      data-testid={dataTestid}
      className="bg-white border rounded-md p-4"
      style={{ borderColor: "#E2E8F0" }}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-md flex items-center justify-center" style={{ background: "#EFF6FF" }}>
            <ScanLine className="h-4 w-4" style={{ color: "#0A2540" }} />
          </div>
          <div>
            <div className="text-sm font-bold tracking-tight">{title}</div>
            <div className="text-xs text-slate-500">{subtitle}</div>
          </div>
        </div>
        {file && (
          <button type="button" onClick={reset} className="p-1 text-slate-400 hover:text-red-600" aria-label="Zurücksetzen">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <label
          className="flex items-center justify-center min-h-[120px] border-2 border-dashed rounded-md p-3 cursor-pointer hover:border-[#0A2540] transition-colors"
          style={{ borderColor: "#CBD5E1", background: "#F8FAFC" }}
        >
          <input
            type="file"
            accept="image/png,image/jpeg,image/webp,application/pdf,.pdf"
            className="hidden"
            onChange={(e) => choose(e.target.files?.[0] || null)}
            data-testid={`${dataTestid}-file`}
          />
          {!preview ? (
            <div className="text-center text-slate-500">
              <Upload className="h-6 w-6 mx-auto mb-1.5 text-slate-400" />
              <div className="text-sm font-semibold text-[#0A2540]">Datei wählen</div>
              <div className="text-xs">PNG · JPEG · WEBP · PDF</div>
            </div>
          ) : preview.pdf ? (
            <div className="text-center">
              <div className="inline-flex items-center justify-center w-10 h-10 rounded-md mb-2" style={{ background: "#FEE2E2" }}>
                <span className="text-[10px] font-bold text-red-700">PDF</span>
              </div>
              <div className="text-xs font-semibold truncate max-w-[180px] mx-auto">{preview.name}</div>
            </div>
          ) : (
            <img src={preview.url} alt="Vorschau" className="max-h-32 mx-auto rounded" />
          )}
        </label>

        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              className="h-10 bg-[#0A2540] hover:bg-[#113A63] text-white flex-1"
              onClick={analyze}
              disabled={!file || analyzing}
              data-testid={`${dataTestid}-analyze`}
            >
              <ScanLine className="h-4 w-4 mr-2" />
              {analyzing ? "Analysiert…" : "Analysieren"}
            </Button>
            {result && (
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-10"
                onClick={apply}
                data-testid={`${dataTestid}-apply`}
              >
                Übernehmen
              </Button>
            )}
          </div>

          {result && (
            <div className="border rounded-md p-2.5 text-xs space-y-1 bg-slate-50" style={{ borderColor: "#E2E8F0" }}>
              <div className={`flex items-center gap-1.5 font-bold uppercase tracking-wider ${confColor}`}>
                {conf === "hoch" ? <CheckCircle2 className="h-3.5 w-3.5" /> : <AlertTriangle className="h-3.5 w-3.5" />}
                Konfidenz: {conf || "—"}
              </div>
              {mode === "auftrag" ? (
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 mt-1">
                  <div><span className="text-slate-500">Auftrags-Nr.:</span> <strong>{result.auftragsnummer || "—"}</strong></div>
                  <div><span className="text-slate-500">KLS-ID:</span> <strong className="mono">{result.kls_id || "—"}</strong></div>
                  <div><span className="text-slate-500">Datum:</span> <strong>{result.datum || "—"}</strong></div>
                  <div><span className="text-slate-500">Telekom:</span> <strong>{result.telekom_konto || "—"}</strong></div>
                </div>
              ) : (
                <div>
                  <div className="text-slate-500">{(result.positionen || []).length} Position(en) erkannt</div>
                  {(result.positionen || []).slice(0, 3).map((p, i) => (
                    <div key={i} className="mono">{p.pos_nummer} · Menge {p.menge}</div>
                  ))}
                  {(result.positionen || []).length > 3 && <div className="text-slate-400">…</div>}
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
